import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../design-system/contexts/ToastContext";
import { Auth } from "aws-amplify";
import { Icon } from "@iconify/react";
import { CognitoHostedUIIdentityProvider } from "@aws-amplify/auth";
import axios from "axios";
import { recaptchaId, webCliendId } from "../../config/config";
import { api } from "../../crud/axios.config";
import awsConfig from "../../config/aws.exports";
import VerifyOtpSignup from "../../components/pages/signup";
import { isValidEmail, parseAuthError } from "../../utils/validation";

// Auth Components
import { AuthLayout, SocialLoginButtons, OrDivider } from "./components";
import "./Auth.css";

const Signup = () => {
  const { success, error: showError } = useToast();
  const query = new URLSearchParams(window.location.search);
  const domain = query.get("redirect");
  const navigate = useNavigate();

  const [state, setState] = useState({
    data: {
      email: "",
      password: "",
      confirmPassword: "",
      inputOtp: "",
    },
    receivedOtp: false,
    loading: false,
    verified: false,
    errors: {
      email: "",
      password: "",
      confirmPassword: "",
      inputOtp: "",
    },
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const doValidate = () => {
    if (state.data.email === undefined || state.data.email === "") {
      let _state = state;
      _state.errors = {
        name: _state.errors.name,
        email: "Please enter your email",
        password: _state.errors.password,
        confirmPassword: _state.errors.confirmPassword,
      };
      setState({ ...state, ..._state });
      return false;
    }

    // Use improved email validation
    if (!isValidEmail(state.data.email)) {
      let _state = state;
      _state.errors = {
        name: _state.errors.name,
        email: "Please enter a valid email address",
        password: _state.errors.password,
        confirmPassword: _state.errors.confirmPassword,
      };
      setState({ ...state, ..._state });
      return false;
    }

    if (state.data.password === undefined || state.data.password === "") {
      let _state = state;
      _state.errors = {
        name: _state.errors.name,
        email: _state.errors.email,
        password: "Please enter your password",
        confirmPassword: _state.errors.confirmPassword,
      };
      setState({ ...state, ..._state });
      return false;
    }

    if ((!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@#$%^&/,><'":;|_~=`+=\-!]).{6,}$/.test(state.data.password)))
    {
      let _state = state;
      _state.errors = {
        name: _state.errors.name,
        email: _state.errors.email,
        password: "Password must contain a minimum of 6 characters, including at least 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character.",
        confirmPassword: _state.errors.confirmPassword,
      };
      setState({ ...state, ..._state });
      return false;
    }

    if (
      state.data.confirmPassword !== state.data.password &&
      state.data.confirmPassword !== ""
    ) {
      let _state = state;
      _state.errors = {
        name: _state.errors.name,
        email: _state.errors.email,
        password: _state.errors.password,
        confirmPassword: "Passwords do not match",
      };
      setState({ ...state, ..._state });
      return false;
    }

    if (
      state.data.confirmPassword === undefined ||
      state.data.confirmPassword === ""
    ) {
      let _state = state;
      _state.errors = {
        name: _state.errors.name,
        email: _state.errors.email,
        password: _state.errors.password,
        confirmPassword: "Please confirm the password",
      };
      setState({ ...state, ..._state });
      return false;
    }
    return true;
  };

  const doFederatedSignIn = async (provider) => {
    Auth.configure({ ...awsConfig, aws_user_pools_web_client_id: webCliendId });
    if (provider === "LinkedIn") {
      const generateState = () => {
        return Math.random().toString(36).substring(2, 15) +
               Math.random().toString(36).substring(2, 15);
      };
      const clientId = "77t75blts25aq1";
      const redirectUri = "/";

      const state = generateState();
      localStorage.setItem("oauth_state", state);

      const scope = encodeURIComponent("r_emailaddress r_liteprofile");
      const linkedInAuthUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&state=${state}`;

      window.location.href = linkedInAuthUrl;
    }
    await Auth.federatedSignIn({ provider });
  };

  const signUp = async () => {
    if(doValidate()){
        setState({...state, loading: true});
        try {
            const isRecaptchaValid = await handleLoaded();
            if (isRecaptchaValid) {
                await Auth.signUp({
                    username: state.data.email,
                    password: state.data.password,
                    attributes: {
                        email: state.data.email,
                    },
                });
                success('Verification code sent to your email successfully. Please verify your account by entering the code.');
                setState({...state, loading: false, receivedOtp: true });
            }
            else{
                showError("Recaptcha verification failed");
            }
        } catch (err) {
            let error = parseAuthError(err);
            if (error === "An account with the given email already exists." || error.includes("already exists")) {
                try {
                    const response = await Auth.resendSignUp(state.data.email);
                    console.log(response);
                    setState({...state, loading: false, receivedOtp: true})
                    success("Verification code sent to your email successfully. Please verify your account by entering the code.");
                } catch (resendErr) {
                    let errorMsg = parseAuthError(resendErr);
                    // If user is already confirmed, show clear message to login
                    if (errorMsg.includes("already confirmed") ||
                        errorMsg.includes("current status is CONFIRMED") ||
                        errorMsg.includes("Cannot resend")) {
                        showError("An account is already registered. Please login.");
                    } else {
                        showError(errorMsg);
                    }
                    console.log(errorMsg);
                    setState({...state, loading: false, receivedOtp: false})
                }
            }
            else if (error.includes("deactivated account")) {
                error = "You cannot sign in because you already have a deactivated account with the same email id!"
                showError(error);
                setState({...state, loading: false});
            } else {
                showError(error);
                setState({...state, loading: false});
            }
        }
    }
  };

  const handleLoaded = () => {
    return new Promise((resolve, reject) => {
      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(recaptchaId, { action: "homepage" })
          .then(async (token) => {
            try {
              const response = await axios.post(
                `${api.authApiUrl}/verify-captcha`,
                {
                  response: token,
                }
              );
              if (response.data.score>0.5) {
                resolve(true);
              } else {
                resolve(false);
              }
            } catch (error) {
              const errorMsg = parseAuthError(error);
              showError(errorMsg);
              reject(error);
            }
          })
          .catch((error) => {
            console.log("Error executing reCAPTCHA:", error);
            reject(error);
          });
      });
    });
  };

  const handlePressEnterOtp = (e) => {
    if (e.keyCode === 13) {
      signUp();
    }
  };

  useEffect(() => {
    // Add reCaptcha
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${recaptchaId}`;
    script.removeAttribute("crossorigin");
    document.body.appendChild(script);
  }, []);

  if (state.receivedOtp) {
    return (
      <VerifyOtpSignup
        state={state}
        user={{ is: true }}
        setState={setState}
        signUp={signUp}
      />
    );
  }

  return (
    <AuthLayout>
      {/* Social Login - All icons in a row */}
      <SocialLoginButtons
        onGoogleClick={() => doFederatedSignIn(CognitoHostedUIIdentityProvider.Google)}
        onAmazonClick={() => doFederatedSignIn(CognitoHostedUIIdentityProvider.Amazon)}
        onLinkedInClick={() => doFederatedSignIn("LinkedIn")}
        showGoogleFull={false}
      />

      <OrDivider />

      {/* Email Input */}
      <div className="auth-form__group">
        <label className="auth-form__label">Email</label>
        <input
          type="email"
          className={`auth-form__input ${state.errors.email ? "auth-form__input--error" : ""}`}
          placeholder="johnd@gmail.com"
          value={state.data.email}
          onKeyDown={handlePressEnterOtp}
          onChange={(e) => {
            setState({
              ...state,
              data: {
                ...state.data,
                email: e.target.value.toLowerCase(),
              },
              errors: {
                ...state.errors,
                email: "",
              },
            });
          }}
          onBlur={() => {
            if (state.data.email === undefined || state.data.email === "") {
              setState({
                ...state,
                errors: {
                  ...state.errors,
                  email: "Please enter your email",
                },
              });
            } else if (!isValidEmail(state.data.email)) {
              setState({
                ...state,
                errors: {
                  ...state.errors,
                  email: "Please enter a valid email address",
                },
              });
            }
          }}
        />
        {state.errors.email && (
          <span className="auth-form__error">{state.errors.email}</span>
        )}
      </div>

      {/* Password Input */}
      <div className="auth-form__group">
        <label className="auth-form__label">Password</label>
        <div className="auth-form__password-wrapper">
          <input
            type={showPassword ? "text" : "password"}
            className={`auth-form__input ${state.errors.password ? "auth-form__input--error" : ""}`}
            placeholder="Enter password"
            value={state.data.password}
            onKeyDown={handlePressEnterOtp}
            onChange={(e) => {
              setState({
                ...state,
                data: {
                  ...state.data,
                  password: e.target.value,
                },
                errors: {
                  ...state.errors,
                  password: "",
                },
              });
            }}
            onBlur={() => {
              if (state.data.password === undefined || state.data.password === "") {
                setState({
                  ...state,
                  errors: {
                    ...state.errors,
                    password: "Please enter your password",
                  },
                });
              }
            }}
          />
          <button
            type="button"
            className="auth-form__password-toggle"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            <Icon
              icon={showPassword ? "mdi-light:eye" : "mdi-light:eye-off"}
              width="20"
              height="20"
            />
          </button>
        </div>
        {state.errors.password ? (
          <span className="auth-form__error">{state.errors.password}</span>
        ) : (
          <span className="auth-form__hint">
            Password must contain a minimum of 6 characters, 1 uppercase, 1 lowercase, 1 number & 1 special character
          </span>
        )}
      </div>

      {/* Confirm Password Input */}
      <div className="auth-form__group">
        <label className="auth-form__label">Confirm Password</label>
        <div className="auth-form__password-wrapper">
          <input
            type={showConfirmPassword ? "text" : "password"}
            className={`auth-form__input ${state.errors.confirmPassword ? "auth-form__input--error" : ""}`}
            placeholder="Enter password again"
            value={state.data.confirmPassword}
            onKeyDown={handlePressEnterOtp}
            onChange={(e) => {
              setState({
                ...state,
                data: {
                  ...state.data,
                  confirmPassword: e.target.value,
                },
                errors: {
                  ...state.errors,
                  confirmPassword: "",
                },
              });
            }}
            onBlur={() => {
              if (state.data.confirmPassword === undefined || state.data.confirmPassword === "") {
                setState({
                  ...state,
                  errors: {
                    ...state.errors,
                    confirmPassword: "Please confirm the password",
                  },
                });
              }
            }}
          />
          <button
            type="button"
            className="auth-form__password-toggle"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            aria-label={showConfirmPassword ? "Hide password" : "Show password"}
          >
            <Icon
              icon={showConfirmPassword ? "mdi-light:eye" : "mdi-light:eye-off"}
              width="20"
              height="20"
            />
          </button>
        </div>
        {state.errors.confirmPassword && (
          <span className="auth-form__error">{state.errors.confirmPassword}</span>
        )}
      </div>

      {/* Sign Up Button */}
      <button
        className="auth-btn"
        onClick={signUp}
        disabled={state.loading}
        style={{ marginTop: "var(--spacing-4)" }}
      >
        {state.loading ? <span className="auth-spinner"></span> : "Sign Up"}
      </button>

      {/* Request Email OTP */}
      <p className="auth-footer-text" style={{ marginTop: "var(--spacing-3)" }}>
        Request{" "}
        <span
          role="button"
          onClick={() => navigate(`/request-otp`, { state: { signUp: true } })}
        >
          Email OTP
        </span>
      </p>

      {/* Terms */}
      <p className="auth-terms">
        By signing up, I agree to KonfHub's Terms & Conditions, Code of Conduct, and Privacy Policy, and consent to sharing my info with the event organiser for event updates.
      </p>

      {/* Sign In Link */}
      <p className="auth-footer-text">
        Already have an account?{" "}
        <span
          role="button"
          onClick={() => navigate("/login")}
        >
          Sign In
        </span>
      </p>
    </AuthLayout>
  );
};

export default Signup;
